import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { submitEvaluation } from "@/lib/api";

const CLAIM_TYPES = ["Hurricane", "Wind", "Storm", "Water", "Pipe Burst", "Fire & Smoke", "Mold", "Roof", "Theft & Vandalism", "Other"];

const PROPERTY_TYPES = [
  { id: "residential", label: "Residential", desc: "Single-family home, townhouse, villa" },
  { id: "commercial", label: "Commercial", desc: "Retail, office, hospitality, industrial" },
  { id: "condo", label: "Condominium / HOA", desc: "Unit-owner or association-level claim" },
];

const CLAIM_STATUSES = [
  { id: "unfiled", label: "Not yet filed", desc: "Damage has occurred — claim has not been opened" },
  { id: "active", label: "Active claim", desc: "Claim is open with the carrier" },
  { id: "denied", label: "Denied", desc: "The carrier has denied the claim" },
  { id: "underpaid", label: "Underpaid / settled-but-incomplete", desc: "A settlement was offered but is insufficient" },
];

const Pill = ({ active, children, onClick, testid, variant = "compact" }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left p-4 sm:p-5 border transition-all duration-400 backdrop-blur-md min-h-[3rem] ${
      active
        ? "border-gold bg-gold/8 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_0_30px_rgba(0,119,255,0.2)]"
        : "border-white/10 hover:border-gold/40 bg-navy-200/40 text-white/70 hover:text-white"
    }`}
  >
    {children}
  </button>
);

export default function EvaluationForm({ variant = "embedded" }) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    claim_type: "", property_type: "", claim_status: "",
    full_name: "", email: "", phone: "", city: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const canAdvance =
    (step === 1 && data.claim_type) ||
    (step === 2 && data.property_type) ||
    (step === 3 && data.claim_status) ||
    (step === 4 && data.full_name && data.email && data.phone);

  const submit = async () => {
    if (!canAdvance) return;
    setSubmitting(true);
    try {
      await submitEvaluation(data);
      setDone(true);
      toast.success("Your evaluation request has been received.");
    } catch {
      toast.error("Submission failed. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="glass-strong p-10 lg:p-14 text-center" data-testid="evaluation-success">
        <div className="w-14 h-14 mx-auto rounded-full border border-gold flex items-center justify-center mb-6" style={{ boxShadow: "0 0 30px rgba(198,168,107,0.4)" }}>
          <Check className="w-6 h-6 text-gold" />
        </div>
        <h3 className="font-display text-3xl text-white mb-4 font-light tracking-[-0.025em]">Thank you.</h3>
        <p className="text-ag-text-secondary leading-[1.65] max-w-md mx-auto font-light">
          Your evaluation request has been received by AG Public Adjusting. A senior member of the firm
          will review your claim and contact you within one business day.
        </p>
        <div className="mt-8 font-mono text-[0.65rem] text-ag-text-muted tracking-[0.28em] uppercase">
          For immediate assistance · call the firm
        </div>
      </div>
    );
  }

  return (
    <div data-testid="evaluation-form" className={`glass-strong p-5 sm:p-8 lg:p-12 ${variant === "standalone" ? "max-w-2xl mx-auto" : ""}`}>
      <div className="flex items-center justify-between mb-6 sm:mb-10">
        <div className="font-mono text-[0.6rem] sm:text-[0.65rem] text-gold tracking-[0.26em] sm:tracking-[0.28em] uppercase" data-testid="evaluation-step-indicator">
          Step {step} / 04
        </div>
        <div className="flex gap-1.5">
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              className={`h-px w-7 sm:w-10 transition-all duration-500 ${n <= step ? "bg-gold" : "bg-white/15"}`}
              style={n <= step ? { boxShadow: "0 0 8px rgba(198,168,107,0.5)" } : {}}
            />
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>
            <h3 className="font-display text-2xl sm:text-3xl text-white mb-3 font-light tracking-[-0.025em]">What type of claim?</h3>
            <p className="text-ag-text-secondary text-sm mb-6 sm:mb-8 font-light">Select the cause of loss most relevant to your property.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CLAIM_TYPES.map((t) => (
                <Pill key={t} active={data.claim_type === t} onClick={() => update("claim_type", t)} testid={`claim-type-${t.toLowerCase().replace(/\s|&/g, "-")}`}>
                  <div className="text-sm font-light tracking-tight">{t}</div>
                </Pill>
              ))}
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>
            <h3 className="font-display text-2xl sm:text-3xl text-white mb-3 font-light tracking-[-0.025em]">Type of property?</h3>
            <p className="text-ag-text-secondary text-sm mb-6 sm:mb-8 font-light">Different property types are governed by different policy structures.</p>
            <div className="space-y-3">
              {PROPERTY_TYPES.map((t) => (
                <Pill key={t.id} active={data.property_type === t.id} onClick={() => update("property_type", t.id)} testid={`property-type-${t.id}`}>
                  <div className="font-display text-lg text-white font-light tracking-[-0.02em]">{t.label}</div>
                  <div className="text-xs text-ag-text-secondary mt-1 font-light">{t.desc}</div>
                </Pill>
              ))}
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>
            <h3 className="font-display text-2xl sm:text-3xl text-white mb-3 font-light tracking-[-0.025em]">Where is the claim now?</h3>
            <p className="text-ag-text-secondary text-sm mb-6 sm:mb-8 font-light">The firm can engage at any stage of the process.</p>
            <div className="space-y-3">
              {CLAIM_STATUSES.map((s) => (
                <Pill key={s.id} active={data.claim_status === s.id} onClick={() => update("claim_status", s.id)} testid={`claim-status-${s.id}`}>
                  <div className="font-display text-lg text-white font-light tracking-[-0.02em]">{s.label}</div>
                  <div className="text-xs text-ag-text-secondary mt-1 font-light">{s.desc}</div>
                </Pill>
              ))}
            </div>
          </motion.div>
        )}

        {step === 4 && (
          <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }}>
            <h3 className="font-display text-2xl sm:text-3xl text-white mb-3 font-light tracking-[-0.025em]">How can we reach you?</h3>
            <p className="text-ag-text-secondary text-sm mb-6 sm:mb-8 font-light">A senior member of the firm will follow up within one business day.</p>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
              <Field label="FULL NAME *">
                <input value={data.full_name} onChange={(e) => update("full_name", e.target.value)} className="ag-input" data-testid="input-name" />
              </Field>
              <Field label="CITY">
                <input value={data.city} onChange={(e) => update("city", e.target.value)} className="ag-input" data-testid="input-city" />
              </Field>
              <Field label="EMAIL *">
                <input type="email" value={data.email} onChange={(e) => update("email", e.target.value)} className="ag-input" data-testid="input-email" />
              </Field>
              <Field label="PHONE *">
                <input type="tel" value={data.phone} onChange={(e) => update("phone", e.target.value)} className="ag-input" data-testid="input-phone" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="BRIEFLY — WHAT HAPPENED?">
                  <textarea value={data.notes} onChange={(e) => update("notes", e.target.value)} rows={3} className="ag-input resize-none" data-testid="input-notes" />
                </Field>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-8 sm:mt-10 pt-6 border-t border-white/8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2 font-mono text-[0.6rem] sm:text-[0.65rem] text-ag-text-muted tracking-[0.22em] uppercase">
          <ShieldCheck className="w-3.5 h-3.5 text-gold flex-shrink-0" />
          <span>No recovery · No fee · Confidential</span>
        </div>
        <div className="flex gap-2 sm:gap-2">
          {step > 1 && (
            <button type="button" onClick={() => setStep((s) => s - 1)} data-testid="evaluation-back-btn" className="btn-ghost-blue !py-3 !px-4 sm:!px-5 !text-[0.62rem] sm:!text-[0.65rem] flex items-center gap-2">
              <ArrowLeft className="w-3 h-3" /> Back
            </button>
          )}
          {step < 4 && (
            <button
              type="button"
              onClick={() => canAdvance && setStep((s) => s + 1)}
              disabled={!canAdvance}
              data-testid="evaluation-next-btn"
              className={`!py-3 !px-5 sm:!px-6 !text-[0.62rem] sm:!text-[0.65rem] flex items-center gap-2 transition-all flex-1 sm:flex-none justify-center ${
                canAdvance ? "btn-gold" : "border border-white/8 bg-white/5 text-white/30 cursor-not-allowed font-display uppercase tracking-[0.16em] font-medium"
              }`}
            >
              Continue <ArrowRight className="w-3 h-3" />
            </button>
          )}
          {step === 4 && (
            <button
              type="button"
              onClick={submit}
              disabled={!canAdvance || submitting}
              data-testid="evaluation-submit-btn"
              className={`!py-3 !px-5 sm:!px-6 !text-[0.62rem] sm:!text-[0.65rem] flex items-center gap-2 flex-1 sm:flex-none justify-center ${
                canAdvance && !submitting ? "btn-gold" : "border border-white/8 bg-white/5 text-white/30 cursor-not-allowed font-display uppercase tracking-[0.16em] font-medium"
              }`}
            >
              {submitting ? "Submitting…" : "Submit Evaluation"}
              {!submitting && <ArrowRight className="w-3 h-3" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[0.6rem] text-ag-text-muted tracking-[0.28em] block mb-1 uppercase">{label}</span>
    {children}
  </label>
);
